import {ModelInfo} from '@/models/model-info';
import {SessionInfo} from '@/models/session-info';
import {SessionMessage} from '@/models/session-message';
import * as modelRepo from '@/repositories/model-repo';
import {
  Button,
  Container,
  SelectProps,
  SpaceBetween,
  Spinner,
} from '@cloudscape-design/components';
import {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useLayoutEffect,
  useState,
} from 'react';
import TextareaAutosize from 'react-textarea-autosize';
import 'regenerator-runtime';
import {OptionsHelper} from '../../common/helpers/options-helper';
import {StorageHelper} from '../../common/helpers/storage-helper';
import {AppContext} from '../../models/app-context';
import styles from '../../styles/chat.module.scss';
import {ChatBotConfiguration, ChatInputState} from './types';
import {getSelectedModelMetadata} from './utils';
import { Truculenta } from 'next/font/google';

export interface ChatInputPanelProps {
  running: boolean;
  setRunning: Dispatch<SetStateAction<boolean>>;
  session: SessionInfo | undefined;
  messageHistory: SessionMessage[];
  setMessageHistory: Dispatch<SetStateAction<SessionMessage[]>>;
  configuration: ChatBotConfiguration;
  setConfiguration: Dispatch<React.SetStateAction<ChatBotConfiguration>>;
  isWaitingForAI: boolean;

  onMessageSent: (message: string) => void;
}

export abstract class ChatScrollState {
  static userHasScrolled = false;
  static skipNextScrollEvent = false;
  static skipNextHistoryUpdate = false;
}

export default function ChatInputPanel(props: ChatInputPanelProps) {
  const appContext = useContext(AppContext);
  // const { transcript, listening, browserSupportsSpeechRecognition } = useSpeechRecognition();
  const [state, setState] = useState<ChatInputState>({
    value: '',
    selectedModel: null,
    selectedModelMetadata: null,
    modelsStatus: 'loading',
  });

  // useEffect(() => {
  //   if (transcript) {
  //     setState(state => ({ ...state, value: transcript }));
  //   }
  // }, [transcript]);

  useEffect(() => {
    (async () => {
      const models = modelRepo.getModels();
      const selectedModelOption = getSelectedModelOption(models);
      const selectedModelMetadata = getSelectedModelMetadata(
        models,
        selectedModelOption
      );

      setState(state => ({
        ...state,
        models: modelRepo.getModels(),
        selectedModel: selectedModelOption,
        selectedModelMetadata,
        modelsStatus: models.length > 0 ? 'finished' : 'error',
      }));
    })();
  }, [appContext, state.modelsStatus]);

  useEffect(() => {
    const onWindowScroll = () => {
      if (ChatScrollState.skipNextScrollEvent) {
        ChatScrollState.skipNextScrollEvent = false;
        return;
      }

      const distanceFromEnd = Math.abs(
        window.innerHeight +
          window.scrollY -
          document.documentElement.scrollHeight
      );

      if (distanceFromEnd > 10) {
        ChatScrollState.userHasScrolled = true;
      } else {
        ChatScrollState.userHasScrolled = false;
      }
    };

    window.addEventListener('scroll', onWindowScroll);

    return () => {
      window.removeEventListener('scroll', onWindowScroll);
    };
  }, []);

  useLayoutEffect(() => {
    if (ChatScrollState.skipNextHistoryUpdate) {
      ChatScrollState.skipNextHistoryUpdate = false;
      return;
    }

    if (!ChatScrollState.userHasScrolled && props.messageHistory.length > 0) {
      ChatScrollState.skipNextScrollEvent = true;
      window.scrollTo({
        top: document.documentElement.scrollHeight + 1000,
        behavior: 'instant',
      });
    }
  }, [props.messageHistory]);

  const handleSendMessage = async () => {
    if (typeof window === 'undefined') return;

    setState(state => ({...state, value: ''}));

    props.onMessageSent(state.value);
  };

  return (
    <SpaceBetween direction="vertical" size="l">
      <Container disableContentPaddings={true} disableHeaderPaddings={true}>
        <div className={styles.input_textarea_container} style={{ float: "left", width: "80%"}}>
          {/* <SpaceBetween size="xxs" direction="horizontal" alignItems="center">
            {browserSupportsSpeechRecognition ? (
              <Button
                iconName={listening ? 'microphone-off' : 'microphone'}
                variant="icon"
                onClick={() =>
                  listening
                    ? SpeechRecognition.stopListening()
                    : SpeechRecognition.startListening()
                }
              />
            ) : (
              <Icon name="microphone-off" variant="disabled" />
            )}
          </SpaceBetween> */}
          <TextareaAutosize
            className={styles.input_textarea}
            maxRows={6}
            minRows={1}
            cols={85}
            spellCheck={true}
            autoFocus
            onChange={e =>
              setState(state => ({...state, value: e.target.value}))
            }
            onKeyDown={e => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                if (!props.isWaitingForAI) {
                  handleSendMessage();
                }
              }
            }}
            value={state.value}
            // placeholder={listening ? 'Listening...' : 'Send a message'}
            placeholder="Send a message."
          />
        </div>
        <div style={{textAlign: 'right', width: '97px', right: '0px', float: 'right', marginTop: '10px', marginRight: '10px'}}>
          <Button
            onClick={handleSendMessage}
            className="ces-red"
            variant="primary"
            iconName="upload"
            disabled={props.isWaitingForAI}
          >
            {props.running ? (
              <>
                Loading&nbsp;&nbsp;
                <Spinner />
              </>
            ) : (
              'Send'
            )}
          </Button>
        </div>
        <div style={{display: 'table', clear: 'both'}}></div>
      </Container>
      {/* <div className={styles.input_controls}>
        <div className={styles.input_controls_selects_1}>
          <Select
            disabled={props.running}
            statusType={state.modelsStatus}
            loadingText="Loading models (might take few seconds)..."
            placeholder="Select a model"
            empty={
              <div>
                No models available. Please make sure you have access to Amazon
                Bedrock or alternatively deploy a self hosted model on SageMaker
                or add API_KEY to Secrets Manager
              </div>
            }
            filteringType="auto"
            selectedOption={state.selectedModel}
            onChange={({detail}) => {
              setState(state => ({
                ...state,
                selectedModel: detail.selectedOption,
                selectedModelMetadata: getSelectedModelMetadata(
                  state.models,
                  detail.selectedOption
                ),
              }));
              if (detail.selectedOption?.value) {
                StorageHelper.setSelectedLLM(detail.selectedOption.value);
              }
            }}
            options={modelsOptions}
          />
        </div>
        <div className={styles.input_controls_right}>
          <SpaceBetween direction="horizontal" size="xxs" alignItems="center">
            <div style={{paddingTop: '1px'}}>
              {props.session && (
                <ConfigDialog
                  sessionId={props.session.id}
                  visible={configDialogVisible}
                  setVisible={setConfigDialogVisible}
                  configuration={props.configuration}
                  setConfiguration={props.setConfiguration}
                />
              )}
              <Button
                disabled={true}
                iconName="settings"
                variant="icon"
                onClick={() => setConfigDialogVisible(true)}
              />
            </div>
            <StatusIndicator type={'in-progress'}>In Progress</StatusIndicator>
          </SpaceBetween>
        </div>
      </div> */}
    </SpaceBetween>
  );
}

function getSelectedModelOption(
  models: ModelInfo[]
): SelectProps.Option | null {
  let selectedModelOption: SelectProps.Option | null = null;
  const savedModel = StorageHelper.getSelectedLLM();

  if (savedModel) {
    const targetModel = models.find(m => m.id === savedModel);

    if (targetModel) {
      selectedModelOption = OptionsHelper.getSelectOptionGroups([
        targetModel,
      ])[0].options[0];
    }
  }

  return selectedModelOption;
}
